// Контракт API пульта (задачи D10 и D11): действия разбора диалога — оценка хода, пример из хода, статьи базы, пробел базы
// и отмена действия.
//
// - `PUT /api/v1/turns/:id/review` (admin) `{ verdict, reasons, note? }` → `{ review, undoId }`: одна оценка на ход и
//   сотрудника, повтор заменяет её; `bad` — только с причинами, «Другое» — только с пояснением;
// - `POST /api/v1/examples` (admin) `{ turnId, reply }` → 201 (новый) или 200 (обновлён) `{ exampleId, source, undoId }`:
//   ответ становится примером для подбора похожих (`source`: текст как в черновике — `ai_qual_approved`, правленный —
//   `human_approved`); запретное в пример не попадает;
// - `GET /api/v1/kb/articles?ids=KB-4.1,KB-6.2` (любой вошедший, до 10 номеров) → `{ articles }`: «Основание хода»;
// - `POST /api/v1/kb/gaps` (admin) `{ turnId, query }` → 201 `{ undoId }`: «Отметить пробел базы»;
// - `POST /api/v1/undo` (admin) `{ undoId }` → `{ ok: true, dialogId }`: отмена своего действия в течение 10 минут.
//
// `undoId` — id строки журнала этого действия. Тост «Отменить» пульт показывает 8 с, сервер держит отмену 10 минут.
//
// Правило контракта: файл импортирует только `zod` и соседей (`./*.js`), типы — `z.infer`.
import { z } from 'zod';

// ───────────────────────────── оценка хода ─────────────────────────────

/** Причины «Плохо» в порядке UX §3: по ним Влад отмечает, что не так в ответе. */
export const BAD_REASONS = [
  'fabricated',
  'not_answered',
  'extra_question',
  'wrong_tone',
  'false_reject',
  'late_handoff',
  'false_ca',
  'other',
] as const;
export const BadReasonSchema = z.enum(BAD_REASONS);
export type BadReason = z.infer<typeof BadReasonSchema>;

/** Причины «Плохо» словами — чипы в поповере разбора. */
export const BAD_REASON_RU: Readonly<Record<BadReason, string>> = {
  fabricated: 'Выдумала факт',
  not_answered: 'Не ответила',
  extra_question: 'Лишний вопрос',
  wrong_tone: 'Не тот тон',
  false_reject: 'Ложный отказ',
  late_handoff: 'Поздно передала',
  false_ca: 'Ложная ЦА',
  other: 'Другое',
};

/** Оценка человека: хорошо или плохо. */
export const REVIEW_VERDICTS = ['good', 'bad'] as const;
export const ReviewVerdictSchema = z.enum(REVIEW_VERDICTS);
export type ReviewVerdict = z.infer<typeof ReviewVerdictSchema>;

/** Сколько знаков в пояснении «Другое»: от 1 до этого. */
export const REVIEW_NOTE_MAX = 500;

/**
 * Оценка хода в ленте: вердикт, причины «Плохо», пояснение («Другое»), моя ли это оценка и чья (короткое имя:
 * «Влад Т.»). У хода может быть оценка каждого сотрудника; в ленте — моя, а если моей нет, то самая свежая чужая.
 */
export const TurnReviewViewSchema = z.object({
  verdict: ReviewVerdictSchema,
  reasons: z.array(BadReasonSchema),
  note: z.string().nullable(),
  byMe: z.boolean(),
  byName: z.string(),
});
export type TurnReviewView = z.infer<typeof TurnReviewViewSchema>;

/** Тело `PUT /turns/:id/review`: причины и пояснение обязательны не всегда — правила проверяет сервер (тексты ниже). */
export const PutReviewBodySchema = z.object({
  verdict: ReviewVerdictSchema,
  reasons: z.array(BadReasonSchema).optional(),
  note: z.string().optional(),
});
export type PutReviewBody = z.infer<typeof PutReviewBodySchema>;

export const PutReviewResponseSchema = z.object({ review: TurnReviewViewSchema, undoId: z.string().min(1) });
export type PutReviewResponse = z.infer<typeof PutReviewResponseSchema>;

/** Тексты отказов оценки (дословно из брифа D11, остальное — по тому же образцу). */
export const REVIEW_ERRORS_RU = {
  needReason: 'Выберите причину: что не так в ответе',
  needNote: 'Опишите причину в поле «Другое»',
  noteTooLong: `Поле «Другое»: не больше ${String(REVIEW_NOTE_MAX)} знаков`,
  reasonsOnlyForBad: 'Причины указываются только для оценки «Плохо»',
  noDraft: 'Оценить можно только ход с черновиком',
  bodyShape: 'Тело запроса — JSON вида {"verdict": "good"} или {"verdict": "bad", "reasons": ["fabricated"], "note": "…"}.',
  badVerdict: 'verdict — «good» (хорошо) или «bad» (плохо).',
  badReasons: `reasons — список причин из: ${BAD_REASONS.join(', ')}.`,
  badNote: 'note — текст пояснения.',
} as const;

// ───────────────────────────── пример из хода ─────────────────────────────

/** Откуда пример из пульта: черновик Анны как есть или ответ, который человек правил. */
export const EXAMPLE_SOURCES_FROM_CONSOLE = ['ai_qual_approved', 'human_approved'] as const;
export const ExampleSourceSchema = z.enum(EXAMPLE_SOURCES_FROM_CONSOLE);
export type ExampleSourceFromConsole = z.infer<typeof ExampleSourceSchema>;

/** Сколько знаков в тексте примера: от 1 до этого. */
export const EXAMPLE_REPLY_MAX = 2000;

export const CreateExampleBodySchema = z.object({ turnId: z.string().min(1), reply: z.string() });
export type CreateExampleBody = z.infer<typeof CreateExampleBodySchema>;

export const ExampleResponseSchema = z.object({
  exampleId: z.string().min(1),
  source: ExampleSourceSchema,
  undoId: z.string().min(1),
});
export type ExampleResponse = z.infer<typeof ExampleResponseSchema>;

/** Тексты отказов примера (кроме «эмбеддинги недоступны» — он в `API_ERRORS_RU` сервера, код `embeddings_unavailable`). */
export const EXAMPLE_ERRORS_RU = {
  bodyShape: 'Тело запроса — JSON вида {"turnId": "…", "reply": "текст ответа"}.',
  emptyReply: 'Напишите текст ответа: пример не может быть пустым',
  replyTooLong: `Ответ длиннее ${String(EXAMPLE_REPLY_MAX)} знаков: сократите его`,
  noDraft: 'Пример можно сохранить только из хода с черновиком',
  noClientText: 'В серии этого хода нет текста клиента — пример не из чего собрать',
  /** «В примере есть запрещённое: проценты, ссылка вне белого списка». */
  forbidden: (labelsRu: readonly string[]): string => `В примере есть запрещённое: ${labelsRu.join(', ')}`,
} as const;

// ───────────────────────────── статьи и пробелы базы ─────────────────────────────

/** Сколько номеров статей можно спросить за раз. */
export const KB_ARTICLES_MAX_IDS = 10;

/** Видимость статьи клиенту: можно и общими словами (внутренние статьи сюда не попадают). */
export const ARTICLE_VISIBILITIES = ['can', 'general'] as const;
export const KB_VISIBILITY_RU = {
  can: 'можно',
  general: 'общими словами',
} as const satisfies Readonly<Record<(typeof ARTICLE_VISIBILITIES)[number], string>>;

export const KbArticleViewSchema = z.object({
  kbId: z.string().min(1),
  title: z.string(),
  /** Блок «Коротко» статьи. */
  shortRu: z.string(),
  visibility: z.enum(ARTICLE_VISIBILITIES),
  visibilityRu: z.enum(['можно', 'общими словами']),
});
export type KbArticleView = z.infer<typeof KbArticleViewSchema>;

export const KbArticlesResponseSchema = z.object({ articles: z.array(KbArticleViewSchema) });
export type KbArticlesResponse = z.infer<typeof KbArticlesResponseSchema>;

/** Сколько знаков в вопросе пробела: от 1 до этого. */
export const KB_GAP_QUERY_MAX = 500;

export const KbGapBodySchema = z.object({ turnId: z.string().min(1), query: z.string() });
export type KbGapBody = z.infer<typeof KbGapBodySchema>;

export const KbGapResponseSchema = z.object({ undoId: z.string().min(1) });
export type KbGapResponse = z.infer<typeof KbGapResponseSchema>;

export const KB_ERRORS_RU = {
  idsRequired: 'ids — номера статей через запятую, например KB-4.1,KB-6.2.',
  idsBadFormat: 'Номера статей — вида KB-4.1, через запятую.',
  idsTooMany: `Не больше ${String(KB_ARTICLES_MAX_IDS)} статей за раз.`,
  gapBodyShape: 'Тело запроса — JSON вида {"turnId": "…", "query": "вопрос клиента"}.',
  gapEmpty: 'Напишите вопрос клиента, на который в базе нет ответа',
  gapTooLong: `Вопрос длиннее ${String(KB_GAP_QUERY_MAX)} знаков: сократите его`,
  gapNoWords: 'В вопросе нет ни слов, ни цифр — запишите его словами',
} as const;

// ───────────────────────────── отмена действия ─────────────────────────────

export const UndoBodySchema = z.object({ undoId: z.string().min(1) });
export type UndoBody = z.infer<typeof UndoBodySchema>;

/** Ответ отмены: `dialogId` — диалог, который надо перечитать (`null`, если действие не про диалог). */
export const UndoResponseSchema = z.object({ ok: z.literal(true), dialogId: z.string().nullable() });
export type UndoResponse = z.infer<typeof UndoResponseSchema>;

export const UNDO_ERRORS_RU = { bodyShape: 'Тело запроса — JSON вида {"undoId": "…"}: id действия из ответа.' } as const;

// ───────────────────────────── коды ошибок ─────────────────────────────

/**
 * Коды ошибок действий разбора и их HTTP-статусы (копия `API_ERROR_STATUS`, `src/console/errors.ts`; совпадение держит
 * `tests/console/review-contract.test.ts`).
 */
export const REVIEW_ERROR_STATUS = {
  /** Тело или параметры не того вида; ход без черновика; запрещённое в примере. */
  bad_request: 400,
  /** В примере есть запрещённое (`verifyReply`). */
  example_forbidden: 400,
  /** Хода нет. */
  not_found: 404,
  /** Эмбеддинги недоступны — пример не сохранён. */
  embeddings_unavailable: 503,
  /** Отменять нечего. */
  undo_not_found: 404,
  /** Отменить можно только своё действие. */
  undo_not_yours: 403,
  /** Срок отмены вышел. */
  undo_expired: 410,
  /** Запись уже изменилась. */
  undo_conflict: 409,
} as const;
export type ReviewErrorCode = keyof typeof REVIEW_ERROR_STATUS;

/** Постоянные тексты этих ошибок (копия `API_ERRORS_RU`). */
export const REVIEW_FIXED_ERRORS_RU = {
  embeddings_unavailable: 'Эмбеддинги недоступны: пример не сохранён. Попробуйте позже — нужен рабочий ключ OpenAI.',
  undo_not_found: 'Нечего отменять: действие не найдено',
  undo_not_yours: 'Отменить можно только своё действие',
  undo_conflict: 'Запись уже изменилась — отмена не применена',
} as const;
